import {
  CAMPAIGNS, CAMPAIGN_ADDRESSES, CAMPAIGN_ADDRESSES_ENABLE, CAMPAIGN_ADDRESSES_DISABLE, CAMPAIGN_IMPORT_CODES,
  CAMPAIGNS_CASHBACK, CAMPAIGN_DETAILS, CAMPAIGN_DETAILS_PATCH, CAMPAIGN_DELETE, CAMPAIGN_EDIT,
  CAMPAIGNS_PAGINATE, CAMPAIGN_PERK_DETAILS, CAMPAIGNS_ENABLE, CAMPAIGNS_DISABLE, PERK_DETAILS,
  CAMPAIGNS_DELETE, NOTIFICATIONS_INCLUDE
} from '@store/types'
import axios from 'axios'
import shortly from 'vuex-shortly'
import moment from 'moment'
import graphQL from '@graphQL'
import CampaignQuery from '@graphQL/queries/Campaign'
import CampaignsQuery from '@graphQL/queries/Campaigns'
import CampaignsCashbackQuery from '@graphQL/queries/CampaignsCashback'
import { toDeepFormData } from '@helpers/cast'
import toLocaleDate from '@helpers/toLocaleDate'
import orderArrayByAttribute from '@helpers/orderArrayByAttribute'

const state = {
  campaigns: [],
  details: {},
  paginate: {},
  perkDetails: {}
}

const getters = {
  [CAMPAIGNS]: ({ campaigns }) => {
    return campaigns.map(campaign => {
      const { start_date: start, end_date: end } = campaign || {}

      const startDate = toLocaleDate(+start)
      const endDate = toLocaleDate(+end)

      const searchString = [
        'name', 'id', 'partner.name'
      ].map(key => (campaign[key] || '').toString().trim().toLowerCase()).join(' ')

      const modified = { ...campaign, start_date: startDate, end_date: endDate, searchString }
      return modified
    })
  },
  [CAMPAIGN_DETAILS]: ({ details }) => {
    const { start_date: start, end_date: end, eventData } = details || {}

    const startDate = toLocaleDate(+start)
    const endDate = toLocaleDate(+end)
    const dailyLimit = details.daily_limit ? details.daily_limit : 2
    const weekLimit = details.week_limit ? details.week_limit : 5
    const monthLimit = details.month_limit ? details.month_limit : 20

    const modified = {
      ...details,
      start_date: startDate,
      end_date: endDate,
      ...eventData,
      daily_limit: dailyLimit,
      week_limit: weekLimit,
      month_limit: monthLimit
    }
    return modified
  },
  [CAMPAIGN_ADDRESSES]: ({ details }) => {
    return details?.addresses?.map(_ => _.id) || []
  },
  [CAMPAIGNS_PAGINATE]: 'paginate',
  [CAMPAIGN_PERK_DETAILS]: 'perkDetails'
}

const mutations = {
  [CAMPAIGNS]: (state, payload) => {
    state.campaigns = orderArrayByAttribute(payload, 'name')
  },
  [CAMPAIGN_DETAILS]: 'details',
  [CAMPAIGN_PERK_DETAILS]: 'perkDetails',
  [CAMPAIGN_ADDRESSES_ENABLE]: (state, payload) => {
    const ids = new Set([...state.details.addresses.map((_) => _.id), ...payload])
    const addresses = Array.from(ids, (id) => ({ id }))
    state.details = { ...state.details, addresses }
  },
  [CAMPAIGN_ADDRESSES_DISABLE]: (state, payload) => {
    const addresses = state.details.addresses.filter(address => !payload.includes(address.id))
    state.details = { ...state.details, addresses }
  },
  [CAMPAIGNS_PAGINATE]: 'paginate'
}

const actions = {
  [CAMPAIGNS]: async ({ commit, getters }, payload = {}) => {
    const { id: perkId } = payload.partner || getters[PERK_DETAILS]
    const { fromPartner, ...rest } = payload
    const response = await graphQL(CampaignsQuery, {
      ...rest,
      ...(fromPartner && { perkId })
    })
    const { data: campaigns, ...paginate } = response.data?.data?.campaigns || {}
    commit(CAMPAIGNS, campaigns)
    commit(CAMPAIGNS_PAGINATE, paginate)
  },

  [CAMPAIGNS_CASHBACK]: async ({ commit, getters }, payload = {}) => {
    const { id: perkId } = payload.partner || getters[PERK_DETAILS]
    const { fromPartner, ...rest } = payload
    const response = await graphQL(CampaignsCashbackQuery, {
      ...rest,
      ...(fromPartner && { perkId })
    })
    const { data: campaigns, ...paginate } = response.data?.data?.campaigns || {}
    commit(CAMPAIGNS, campaigns)
    commit(CAMPAIGNS_PAGINATE, paginate)
  },

  [CAMPAIGN_DETAILS]: async ({ commit }, payload) => {
    const { campaignId: id, type } = payload || {}
    const response = await graphQL(CampaignQuery, { id, isEditing: type === 'edit' })
    const campaign = response.data?.data?.campaign

    commit(CAMPAIGN_DETAILS, campaign)
    commit(CAMPAIGN_PERK_DETAILS, campaign)
  },

  [CAMPAIGN_EDIT]: async ({ commit, dispatch }, { formData, perkId } = {}) => {
    const startDate = moment(formData?.start_date, 'DD/MM/YYYY HH:mm')
    const newStartDate = startDate.isValid() ? startDate.format('YYYY-MM-DD HH:mm:ss') : ''

    const endDate = moment(formData?.end_date, 'DD/MM/YYYY HH:mm')
    const newEndDate = endDate.isValid() ? endDate.format('YYYY-MM-DD HH:mm:ss') : ''
    const commission = formData.commission
    const { id } = formData

    if (!Array.isArray(formData.howtouse)) formData.howtouse = []

    const method = id ? 'put' : 'post'

    const modified = { ...formData, start_date: newStartDate, end_date: newEndDate, commission }
    const response = await axios[method](`/perks/${perkId}/campaign${id ? '/' + id : ''}`, modified)
    if (response.data.success) {
      if (method === 'post') { commit(CAMPAIGN_DETAILS, response.data.data) }
      dispatch(NOTIFICATIONS_INCLUDE, {
        type: 'success',
        message: `Oferta ${method === 'post' ? 'criada' : 'alterada'} com sucesso!`
      })

      if (formData['vouchersFile']) {
        dispatch(CAMPAIGN_IMPORT_CODES, formData['vouchersFile'])
      }
    }
    return response
  },

  [CAMPAIGN_DETAILS_PATCH]: async ({ commit, state }, { id, payload }) => {
    const response = await axios.patch(`/perks/${state.details.partner_id}/campaign/${id}`, payload)
    return response
  },

  [CAMPAIGN_DELETE]: async ({ dispatch }, { campaignId, perkId } = {}) => {
    await axios.delete(`/perks/${perkId}/campaign/${campaignId}`)
    await dispatch(NOTIFICATIONS_INCLUDE, {
      type: 'success',
      message: 'Oferta excluída com sucesso!'
    })
  },

  [CAMPAIGNS_ENABLE]: async ({ dispatch, getters }, payload) => {
    const { id: perkId } = getters[PERK_DETAILS]
    const ids = payload.map(campaign => campaign.id)
    await axios.patch('/campaigns/group', {
      ids,
      status: '1'
    })
    await dispatch(CAMPAIGNS, { perkId })
  },

  [CAMPAIGNS_DISABLE]: async ({ dispatch, getters }, payload) => {
    const { id: perkId } = getters[PERK_DETAILS]
    const ids = payload.map(campaign => campaign.id)
    await axios.patch('/campaigns/group', {
      ids,
      status: '0'
    })
    await dispatch(CAMPAIGNS, { perkId })
  },

  [CAMPAIGNS_DELETE]: async ({ dispatch, getters }, payload) => {
    const { id: perkId } = getters[PERK_DETAILS]
    const ids = payload.map(campaign => campaign.id)
    await axios.post('/campaigns/action', {
      action: 'delete',
      ids
    })
    await dispatch(CAMPAIGNS, { perkId })
  },

  [CAMPAIGN_ADDRESSES_ENABLE]: ({ commit }, addresses) => {
    commit(CAMPAIGN_ADDRESSES_ENABLE, addresses)
  },

  [CAMPAIGN_ADDRESSES_DISABLE]: ({ commit }, addresses) => {
    commit(CAMPAIGN_ADDRESSES_DISABLE, addresses)
  },

  [CAMPAIGN_IMPORT_CODES]: async ({ state, commit, dispatch }, file) => {
    const formData = toDeepFormData({ file })
    try {
      const response = await axios.post(`perks/${state.details.partner_id}/campaign/${state.details.id}/voucher`, formData)
      if (response.data.success) {
        dispatch(NOTIFICATIONS_INCLUDE, {
          type: 'success',
          message: 'Arquivo importado com sucesso!'
        })
      } else {
        dispatch(NOTIFICATIONS_INCLUDE, new Error('Não foi possível importar o arquivo.'))
      }
      return response
    } catch (e) {
      dispatch(NOTIFICATIONS_INCLUDE, new Error('Não foi possível importar o arquivo.'))
    }
  }
}

export default shortly({ state, getters, mutations, actions })



// WEBPACK FOOTER //
// ./src/store/modules/campaigns.js